/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useRef, useCallback, useMemo, ReactNode } from 'react';

interface ScrollPositionContextValue {
  /** Get saved scroll position for a key */
  getScrollPosition: (key: string) => number;
  /** Save scroll position for a key */
  setScrollPosition: (key: string, position: number) => void;
  /** Clear scroll position for a key */
  clearScrollPosition: (key: string) => void;
  /** Clear all scroll positions */
  clearAllScrollPositions: () => void;
}

const ScrollPositionContext = createContext<ScrollPositionContextValue>({
  getScrollPosition: () => 0,
  setScrollPosition: () => {},
  clearScrollPosition: () => {},
  clearAllScrollPositions: () => {},
});

export function useScrollPositionContext() {
  return useContext(ScrollPositionContext);
}

interface ScrollPositionProviderProps {
  children: ReactNode;
}

/**
 * Provider that stores scroll positions in memory.
 * Positions persist across component unmount/remount but are cleared on page refresh.
 */
export function ScrollPositionProvider({ children }: ScrollPositionProviderProps) {
  // Use ref to avoid re-renders when scroll positions change
  const scrollPositions = useRef<Map<string, number>>(new Map());

  const getScrollPosition = useCallback((key: string): number => {
    return scrollPositions.current.get(key) ?? 0;
  }, []);

  const setScrollPosition = useCallback((key: string, position: number): void => {
    scrollPositions.current.set(key, position);
  }, []);

  const clearScrollPosition = useCallback((key: string): void => {
    scrollPositions.current.delete(key);
  }, []);

  const clearAllScrollPositions = useCallback((): void => {
    scrollPositions.current.clear();
  }, []);

  const value = useMemo(
    () => ({
      getScrollPosition,
      setScrollPosition,
      clearScrollPosition,
      clearAllScrollPositions,
    }),
    [getScrollPosition, setScrollPosition, clearScrollPosition, clearAllScrollPositions]
  );

  return <ScrollPositionContext.Provider value={value}>{children}</ScrollPositionContext.Provider>;
}

export default ScrollPositionContext;
